import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Autocomplete,
  Box,
  Button,
  Typography,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import api from "../../services/api";

/**
 * ServiciosDataTable - Tabla editable para gestionar servicios en evento
 * Permite agregar, editar y eliminar servicios con cálculo automático
 */
const ServiciosDataTable = ({ value = [], onChange, error, helperText }) => {
  const [servicios, setServicios] = useState([]);
  const [serviciosDisponibles, setServiciosDisponibles] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [loading, setLoading] = useState(false);

  // Cargar servicios disponibles
  useEffect(() => {
    loadServicios();
  }, []);

  // Actualizar cuando cambia value (prop)
  useEffect(() => {
    if (Array.isArray(value)) {
      setServicios(value);
    }
  }, [value]);

  const loadServicios = async () => {
    try {
      setLoading(true);
      console.log("[ServiciosDataTable] Cargando servicios...");
      const response = await api.get("/servicios");
      console.log("[ServiciosDataTable] Response:", response.data);
      // servicioService normaliza la respuesta en el interceptor
      // response.data debería ser { servicios: [...] }
      const list =
        response.data?.servicios ||
        (Array.isArray(response.data) ? response.data : []);
      console.log("[ServiciosDataTable] Lista final:", list);
      setServiciosDisponibles(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("[ServiciosDataTable] Error cargando servicios:", error);
      setServiciosDisponibles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddServicio = () => {
    const newServicio = {
      servicio_id: null,
      cantidad: 1,
      precio_unitario: 0,
      total: 0,
    };
    const updatedServicios = [...servicios, newServicio];
    setServicios(updatedServicios);
    onChange(updatedServicios);
    setEditingIndex(servicios.length);
  };

  const handleDeleteServicio = (index) => {
    const updatedServicios = servicios.filter((_, i) => i !== index);
    setServicios(updatedServicios);
    onChange(updatedServicios);
  };

  const handleCellChange = (index, field, value) => {
    const updatedServicios = [...servicios];
    updatedServicios[index][field] = value;

    // Calcular total automáticamente
    if (field === "cantidad" || field === "precio_unitario") {
      updatedServicios[index].total =
        (updatedServicios[index].cantidad || 0) *
        (updatedServicios[index].precio_unitario || 0);
    }

    // Si cambia el servicio, actualizar precio
    if (field === "servicio_id" && value) {
      const servicioSeleccionado = serviciosDisponibles.find(
        (s) => s.id === value,
      );
      if (servicioSeleccionado) {
        updatedServicios[index].precio_unitario =
          servicioSeleccionado.precio || 0;
        updatedServicios[index].total =
          (updatedServicios[index].cantidad || 1) *
          (servicioSeleccionado.precio || 0);
      }
    }

    setServicios(updatedServicios);
    onChange(updatedServicios);
  };

  const handleSelectServicio = (index, servicioId) => {
    handleCellChange(index, "servicio_id", servicioId);
    setEditingIndex(null);
  };

  const getTotalServicios = () => {
    return servicios.reduce((sum, s) => sum + (s.total || 0), 0);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
    }).format(value || 0);
  };

  const getNombreServicio = (servicioId) => {
    const servicio = serviciosDisponibles.find((s) => s.id === servicioId);
    return servicio?.nombre || "Seleccionar servicio";
  };

  return (
    <Box sx={{ width: "100%", maxWidth: 800, mx: "auto" }}>
      <Typography
        variant="h6"
        sx={{ mb: 2, fontWeight: 600, color: "#00C9FF" }}
      >
        Servicios
      </Typography>

      <TableContainer
        component={Paper}
        sx={{
          mb: 2,
          boxShadow: "0 4px 20px rgba(0, 201, 255, 0.15)",
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: "#131829",
          border: "1px solid rgba(0, 201, 255, 0.2)",
        }}
      >
        <Table size="small" sx={{ tableLayout: "fixed" }}>
          <TableHead
            sx={{
              background: "linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)",
              "& .MuiTableCell-head": {
                background: "transparent",
                color: "#FFFFFF",
                fontWeight: "700",
                fontSize: "0.9rem",
                padding: "16px 12px",
                borderBottom: "2px solid rgba(0, 201, 255, 0.3)",
              },
            }}
          >
            <TableRow sx={{ height: 56 }}>
              <TableCell
                sx={{
                  fontWeight: "700",
                  color: "white",
                  fontSize: "0.9rem",
                  padding: "16px 12px",
                }}
              >
                Servicio
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: "700",
                  color: "white",
                  fontSize: "0.9rem",
                  padding: "16px 12px",
                }}
              >
                Cantidad
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: "700",
                  color: "white",
                  fontSize: "0.9rem",
                  padding: "16px 12px",
                }}
              >
                Precio Unitario
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: "700",
                  color: "white",
                  fontSize: "0.9rem",
                  padding: "16px 12px",
                }}
              >
                Total
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontWeight: "700",
                  color: "white",
                  fontSize: "0.9rem",
                  padding: "16px 12px",
                }}
              >
                ⚙️
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {servicios.map((servicio, index) => (
              <TableRow
                key={`servicio-${index}`}
                sx={{
                  "&:hover": {
                    bgcolor: "rgba(0, 201, 255, 0.08)",
                  },
                  bgcolor:
                    editingIndex === index
                      ? "rgba(0, 201, 255, 0.12)"
                      : "#1a1f36",
                  transition: "background-color 0.2s ease",
                }}
              >
                {/* Servicio */}
                <TableCell>
                  {editingIndex === index ? (
                    <Autocomplete
                      size="small"
                      options={serviciosDisponibles}
                      getOptionLabel={(option) => option?.nombre || ""}
                      value={
                        serviciosDisponibles.find(
                          (s) => s.id === servicio.servicio_id,
                        ) || null
                      }
                      onChange={(e, newValue) =>
                        handleSelectServicio(index, newValue?.id || null)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Buscar servicio..."
                          error={!servicio.servicio_id}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              bgcolor: "#0a0e27",
                              color: "#FFFFFF",
                              "& fieldset": {
                                borderColor: !servicio.servicio_id
                                  ? "#FF5252"
                                  : "rgba(0, 201, 255, 0.3)",
                              },
                              "&:hover fieldset": {
                                borderColor: "#00C9FF",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#00C9FF",
                              },
                            },
                            "& .MuiInputBase-input": {
                              color: "#FFFFFF",
                            },
                            "& .MuiInputBase-input::placeholder": {
                              color: "#B0BEC5",
                              opacity: 1,
                            },
                          }}
                        />
                      )}
                      loading={loading}
                      sx={{
                        minWidth: 200,
                        "& .MuiAutocomplete-popupIndicator": {
                          color: "#B0BEC5",
                        },
                        "& .MuiAutocomplete-clearIndicator": {
                          color: "#B0BEC5",
                        },
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        cursor: "pointer",
                        p: 1,
                        borderRadius: 1,
                        "&:hover": {
                          bgcolor: "rgba(0, 201, 255, 0.1)",
                        },
                      }}
                      onClick={() => setEditingIndex(index)}
                    >
                      <span style={{ color: "#FFFFFF" }}>
                        {getNombreServicio(servicio.servicio_id)}
                      </span>
                      <EditIcon fontSize="small" sx={{ color: "#00C9FF" }} />
                    </Box>
                  )}
                </TableCell>

                {/* Cantidad */}
                <TableCell align="right">
                  <TextField
                    type="number"
                    size="small"
                    value={servicio.cantidad || 1}
                    onChange={(e) =>
                      handleCellChange(
                        index,
                        "cantidad",
                        parseInt(e.target.value) || 0,
                      )
                    }
                    sx={{
                      maxWidth: 80,
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "#0a0e27",
                        color: "#FFFFFF",
                        "& fieldset": { borderColor: "rgba(0, 201, 255, 0.3)" },
                        "&:hover fieldset": { borderColor: "#00C9FF" },
                        "&.Mui-focused fieldset": { borderColor: "#00C9FF" },
                      },
                      "& .MuiInputBase-input": {
                        color: "#FFFFFF",
                        textAlign: "center",
                      },
                    }}
                    inputProps={{ min: 1, max: 9999 }}
                  />
                </TableCell>

                {/* Precio Unitario */}
                <TableCell align="right">
                  <TextField
                    type="number"
                    size="small"
                    value={servicio.precio_unitario || 0}
                    onChange={(e) =>
                      handleCellChange(
                        index,
                        "precio_unitario",
                        parseFloat(e.target.value) || 0,
                      )
                    }
                    sx={{
                      maxWidth: 100,
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "#0a0e27",
                        color: "#FFFFFF",
                        "& fieldset": { borderColor: "rgba(0, 201, 255, 0.3)" },
                        "&:hover fieldset": { borderColor: "#00C9FF" },
                        "&.Mui-focused fieldset": { borderColor: "#00C9FF" },
                        "&.Mui-disabled": { bgcolor: "#1a1f36" },
                      },
                      "& .MuiInputBase-input": {
                        color: "#FFFFFF",
                        textAlign: "right",
                      },
                      "& .Mui-disabled .MuiInputBase-input": {
                        color: "#B0BEC5",
                        WebkitTextFillColor: "#B0BEC5",
                      },
                    }}
                    inputProps={{ step: 0.01, min: 0 }}
                    disabled={servicio.servicio_id !== null}
                  />
                </TableCell>

                {/* Total */}
                <TableCell
                  align="right"
                  sx={{ fontWeight: "bold", color: "#92FE9D" }}
                >
                  {formatCurrency(servicio.total || 0)}
                </TableCell>

                {/* Acciones */}
                <TableCell align="center">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteServicio(index)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}

            {servicios.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={5}
                  align="center"
                  sx={{ py: 3, bgcolor: "#1a1f36" }}
                >
                  <Typography sx={{ color: "#B0BEC5" }}>
                    {loading
                      ? "Cargando servicios..."
                      : 'No hay servicios agregados. Haz clic en "Agregar Servicio"'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {serviciosDisponibles.length === 0 &&
              servicios.length === 0 &&
              !loading && (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    align="center"
                    sx={{ py: 3, bgcolor: "rgba(255, 193, 7, 0.1)" }}
                  >
                    <Typography sx={{ color: "#FFC107" }} variant="body2">
                      ⚠️ No hay servicios disponibles en la base de datos
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Botón Agregar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          onClick={handleAddServicio}
          disabled={serviciosDisponibles.length === 0}
          sx={{
            background: "linear-gradient(45deg, #1a237e 30%, #0d47a1 90%)",
            boxShadow: "0 3px 5px 2px rgba(26, 35, 126, .3)",
            borderRadius: 1,
          }}
        >
          + Agregar Servicio
        </Button>

        <Box
          sx={{
            textAlign: "right",
            background:
              "linear-gradient(135deg, rgba(26, 35, 126, 0.3) 0%, rgba(13, 71, 161, 0.3) 100%)",
            border: "1px solid rgba(0, 201, 255, 0.2)",
            p: 2,
            borderRadius: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: "#B0BEC5" }}>
            Total Servicios:
          </Typography>
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", color: "#00C9FF", fontSize: "1.3rem" }}
          >
            {formatCurrency(getTotalServicios())}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Typography color="error" variant="caption">
          {helperText || "Error en servicios"}
        </Typography>
      )}
    </Box>
  );
};

export default ServiciosDataTable;
